import {ResearchLitElement, html, css} from '@core/research-lit-element';
import {componentMap} from './modules/research-componentmap.js';

import '@bog-design-system/bd-router';
import {initRouting} from '@bog-design-system/bd-router';
import config from './config/config.js';

import './common/header/research-header.js';
import './common/footer/research-footer.js';
import '../src/common/router/research-router.js';

import './modules/main/research-main.js';
import './modules/catalog/research-catalog.js';
import './modules/detail/research-detail.js';
import '@common/analytics/research-user-consent.js';

import {setMetaTags} from './common/research-meta-setter';

class ResearchApp extends ResearchLitElement {
    static get is() {
        return 'research-app';
    }

    static get styles() {
        return css`
            :host {
                --content-max-width: 1296px;
                --section-space: var(--space-48);
                --border-space: var(--space-48);
                --section-title-font-size: 36px;
                --section-title-space: var(--space-16);
            }

            .hidden {
                display: none;
            }

            @media (max-width: 1024px) {
                :host {
                    --border-space: var(--space-36);
                }
            }

            @media (max-width: 768px) {
                :host {
                    --section-space: var(--space-40);
                    --border-space: calc(var(--space-24) + var(--space-4));
                }
            }

            @media (max-width: 600px) {
                :host {
                    --border-space: var(--space-24);
                }
            }
        `;
    }

    render() {
        return html`
            <research-router
                    .route="${this.route}"
                    .pattern="${`/:lang/:segment/:action`}"
                    @route-changed="${this._onRouteChanged}">
            </research-router>
            <div class="research" id="main">
                <research-header
                        id="header"
                        .active="${this.route}"
                ></research-header>
                <div id="content" class="content"></div>
                <research-footer id="footer" class=""></research-footer>
            </div>
            <research-user-consent id="consent"></research-user-consent>
        `;
    }
    static get properties() {
        return {
            tree: {
                type: Object,
            },
            route: {
                type: String,
            },
            lang: {
                type: String,
            },
            pathEndpoints: {
                type: Array,
            },
        };
    }

    constructor() {
        super();
        this.defaultLang = 'ka';
        this._initRouteChangeListener();
    }

    connectedCallback() {
        super.connectedCallback();
        const sdk = document.createElement('script');
        sdk.src = config.ssoSdkUrl;
        document.head.appendChild(sdk);


        const baseUrl = window.location.origin + '/';
        if (window.location.href === baseUrl) {
            window.location.href = `/ka`;
        }
    }

    _getCleanPath(newRoute) {
        let path = newRoute;
        if (path.endsWith('/')) {
            path = path.slice(0, -1);
        }
        return path;
    }

    _initRouteChangeListener() {
        window.addEventListener('bd-router-updated', (event) => {
            const route = event.detail.pathname;
            this.route = route;
            if (this._previousRoute === route) {
                return;
            }
            this._previousRoute = route;
            this._routeChanged(route);
        });

        initRouting();
    }

    setLanguageFromUrl(params) {
        const lang = params[1];
        if (config.languages.includes(lang)) {
            this.lang = lang;
            localStorage.setItem('language', lang);
        }
    }

    async getTree() {
        const treeCache = JSON.parse(localStorage.getItem('tree')) || {};
        const checksum = treeCache.checksum ? treeCache.checksum : null;
        const res = await this.apiCall('tree/getTree', 'POST', {checksum: checksum});
        if (res.json) {
            this.tree = this._flattenTree(res.json);
            localStorage.setItem('tree', JSON.stringify(this.tree));
        } else {
            this.tree = treeCache;
        }
    }

    includesLang(params) {
        const lastIndex = params[1]?.indexOf('?') >= 0 ? params[1]?.indexOf('?') : params[1]?.length;
        return params.length === 2 && !config.languages.includes(params[1]) && params[1].substring(0, lastIndex);
    }

    async _routeChanged(newRoute) {
        if (!this.tree) {
            await this.getTree();
        }

        const path = this._getCleanPath(newRoute);
        const params = path.split('/');
        if (params.length <= 2 && !this.includesLang(params)) {
            const lang = localStorage.getItem('language');
            this.lang = lang ? lang : this.defaultLang;
            localStorage.setItem('language', this.lang);
            this._renderPage(this.tree.root.childs[0]);
            return;
        }
        if (!this._setLang(params[1])) {
            return;
        }
        this.setLanguageFromUrl(params);
        params.splice(1, 1);
        const lastParam = params[params.length - 1];
        if (lastParam.includes('?')) {
            const index = lastParam.indexOf('?');
            params[params.length - 1] = lastParam.substring(0, index);
            if (params[params.length - 1] === '') {
                params.pop();
            }
        }

        const node = this._findNode(params);
        this._renderPage(node);
    }

    _findNode(path) {
        if (!path.length) {
            return this.tree.root.childs[0];
        }
        if (this.compareArrays(path, ['', 'research', 'main'])) {
            path = ['', 'research'];
        }
        let curNode = this.tree.root;

        for (let i = 0; i < path.length; i++) {
            let found = false;
            for (const node of curNode.childs) {
                if (node.endPoint === path[i]) {
                    found = true;
                    curNode = node;
                    break;
                }
            }

            if (!found && !componentMap[curNode.contentType].hasParams) {
                return null;
            }
            if (!found && componentMap[curNode.contentType].hasParams) {
                curNode.params = path.slice(i);
                break;
            }
        }
        return curNode;
    }

    _flattenTree(tree, endPointNodeMap = {}) {
        endPointNodeMap[tree.endPoint] = tree;
        for (const child of tree.childs) {
            this._flattenTree(child, endPointNodeMap);
        }
        return endPointNodeMap;
    }

    _renderPage(node) {
        if (!node || !componentMap[node.contentType]) {
            node = this._findNode(['', '404']);
        }

        if (!componentMap[node.contentType].hideHeader) {
            this.$.header.classList.remove('hidden');
        }

        if (!componentMap[node.contentType].hideFooter) {
            this.$.footer.classList.remove('hidden');
        }
        window.scrollTo({
            top: 0,
        });
        if (node[this.lang].url) {
            window.location.replace(encodeURI(node[this.lang].url));
        }

        setMetaTags(node, this.lang);

        const elem = document.querySelector('link[rel=canonical]');
        if (elem) {
            elem.remove();
        }

        componentMap[node.contentType].action()
            .then(() => {
                const element = document.createElement(componentMap[node.contentType].name);
                element.classList.add('page-component');
                element.ref = node[this.lang].content;
                if (node.params) {
                    for (let i = 0; i < node.params.length; i++) {
                        node.params[i] = decodeURI(node.params[i]);
                    }
                    element.params = node.params;
                }
                if (node.queryParams) {
                    element.queryParams = node.queryParams;
                }
                this.$.content.innerHTML = '';
                this.$.content.appendChild(element);
            })
            .catch((err) => {
                console.warn(err);
                this._renderPage(this._findNode(['', '404']));
            });
    }

    _setLang(lang) {
        const currLang = lang?.toLowerCase();
        const isSupportedLanguage = config.languages.includes(currLang);

        this.lang = isSupportedLanguage ? currLang : this.defaultLang;

        if (!isSupportedLanguage) {
            this._renderPage(this._findNode(['', '404']));
            // window.location.replace(encodeURI(`/${this.lang}/research/404`));
            return false;
        }

        return true;
    }

    _onRouteChanged(event) {
        const target = event.target;
        if (!target.active) return;
        this.tail = target.tail;
        this.action = target.params?.action?.toUpperCase();
    }

    compareArrays(a, b) {
        return a.length === b.length &&
            a.every((element, index) => element === b[index]);
    }
}

window.customElements.define(ResearchApp.is, ResearchApp);
